import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ViewColumnDetail } from '@msdk/definitions'
import { COLORS, IconSelector, MenuItem, styled, Switch, Typography } from '@msdk/ui'
import React from 'react'

import { CustomViewColumnDefinition, useDynamicViewContext } from '../context'

const StyledMenuItem = styled(MenuItem, { shouldForwardProp: (prop) => prop !== '$isDragging' })<{
  $isDragging?: boolean
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};

  &:hover {
    background-color: inherit;
  }
`

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  gap: 4px;
`

const StyledSwitch = styled(Switch)`
  cursor: pointer;
`

type Props = {
  column: CustomViewColumnDefinition
}

export function SortableItem({ column }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.id })
  const { getColumnHeaderInfo, toggleColumnVisibility, showExtra } = useDynamicViewContext()

  /**
   * Override styles with transform and transition properties from the d&d library.
   * Ensures smoother operation.
   */
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <StyledMenuItem
      disableRipple
      key={column.id}
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      {...attributes}
      {...listeners}
    >
      <Container>
        <StyledSwitch
          checked={
            (showExtra && column.detail === ViewColumnDetail.Extra && column.visibility === true) ||
            (column.detail === ViewColumnDetail.Default && column.visibility === true)
          }
          onChange={() => toggleColumnVisibility(column)}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          color="primary"
        />
        <Typography variant="body1" color={COLORS.text.secondary}>
          {getColumnHeaderInfo(column).title}
        </Typography>
      </Container>
      <IconSelector iconName="DragIndicator" color="primary" />
    </StyledMenuItem>
  )
}
